// ─────────────────────────────────────────────────────────────────────────────
// Event type discriminants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Informational events — no user action needed, no form dialog
 */
export type InformationalEventType =
  | 'payment_created'
  | 'payment_captured'
  | 'payment_settled'
  | 'payment_canceled'
  | 'refund_issued'
  | 'refund_initiated'
  | 'refund_declined'
  | 'refund_processing'
  | 'refund_completed'
  | 'refund_failed'
  | 'comment_added'
  | 'authorization_extended'
  | 'authorization_incremented'
  | 'evidence_submitted'
  | 'dispute_under_review'
  | 'dispute_won'
  | 'dispute_lost'

/**
 * Action-required events — each type maps to a specific form dialog:
 * - dispute_received       → DisputeResponseForm
 * - refund_requested       → RefundApprovalForm
 * - chargeback_received    → ChargebackResponseForm
 * - fraud_alert            → FraudAlertForm
 * - payment_under_review   → PaymentReviewForm
 */
export type ActionRequiredEventType =
  | 'dispute_received'
  | 'refund_requested'
  | 'chargeback_received'
  | 'fraud_alert'
  | 'payment_under_review'

export type ActivityEventType = InformationalEventType | ActionRequiredEventType

// ─────────────────────────────────────────────────────────────────────────────
// Event severity
// ─────────────────────────────────────────────────────────────────────────────

export type ActivityEventSeverity = 'info' | 'warning' | 'error'

// ─────────────────────────────────────────────────────────────────────────────
// Base event shape
// ─────────────────────────────────────────────────────────────────────────────

/** Icon shown in the timeline circle for each event group */
export type ActivityEventIcon =
  | 'zap'
  | 'credit-card'
  | 'shield-alert'
  | 'receipt'
  | 'message-square'
  | 'refresh-cw'
  | 'file-text'
  | 'check-circle'
  | 'arrow-left'

/** Sub-event shown under an expanded timeline group */
export interface ActivitySubEvent {
  id: string
  title: string
  timestampDisplay: string
}

/** Info card shown inside an expanded timeline group (e.g. dispute details) */
export interface ActivityStatusCard {
  title: string
  timestampDisplay: string
  description?: string
}

/** Additional action button shown alongside the primary CTA */
export interface ActivityAdditionalAction {
  label: string
  variant: 'outline' | 'more'
}

export interface OperatorResponse {
  /** Human-readable label, e.g. "Resolved · False positive" */
  label: string
  notes?: string
  actor: string
  timestampDisplay: string
}

export interface BaseActivityEvent {
  id: string
  type: ActivityEventType
  title: string
  description?: string
  /** ISO 8601 string — used for sorting */
  timestamp: string
  /** Pre-formatted display string, e.g. "09/24/2024 12:41" */
  timestampDisplay: string
  severity: ActivityEventSeverity
  /** Optional expandable sub-details (same shape as BaseTimelineItem) */
  details?: Array<{ action: string; timestamp: string }>
  /** Icon for the timeline circle */
  icon?: ActivityEventIcon
  /** Child events shown under this group when expanded */
  subEvents?: ActivitySubEvent[]
  /** Info card shown inside expanded view */
  statusCard?: ActivityStatusCard
  /** Win probability percentage (0-100) for dispute events */
  winProbability?: number
  /** Additional action buttons (shown next to primary CTA) */
  additionalActions?: ActivityAdditionalAction[]
  /** Operator's response bubble, shown right-aligned after an action is completed */
  operatorResponse?: OperatorResponse
}

// ─────────────────────────────────────────────────────────────────────────────
// Discriminated union — informational vs action-required
// ─────────────────────────────────────────────────────────────────────────────

export interface InformationalActivityEvent extends BaseActivityEvent {
  type: InformationalEventType
  requiresAction: false
}

export interface ActionRequiredActivityEvent extends BaseActivityEvent {
  type: ActionRequiredEventType
  requiresAction: true
  /** Short label for the CTA button, e.g. "Respond to dispute" */
  actionLabel: string
  /** Whether the user has already completed the action */
  actionCompleted?: boolean
}

export type ActivityEvent = InformationalActivityEvent | ActionRequiredActivityEvent

// ─────────────────────────────────────────────────────────────────────────────
// Type guard
// ─────────────────────────────────────────────────────────────────────────────

export function isActionRequired(event: ActivityEvent): event is ActionRequiredActivityEvent {
  return event.requiresAction === true
}

// ─────────────────────────────────────────────────────────────────────────────
// ActivitySection component props
// ─────────────────────────────────────────────────────────────────────────────

export interface ActivitySectionProps {
  events: ActivityEvent[]
  isExpanded?: boolean
  onToggle?: () => void
  isDetailView?: boolean
  title?: string
  className?: string
  /** Optional callback for action-required events. If not provided, internal ActionDialog is used. */
  onActionClick?: (event: ActionRequiredActivityEvent) => void
  /** Whether to show the "Add comment" button in the header. Default: true */
  showAddComment?: boolean
  /** When true, the section scrolls itself into view on mount */
  scrollIntoView?: boolean
  /** If set, auto-expands all events so the target event is visible */
  autoExpandEventId?: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Form data types — one per action dialog
// ─────────────────────────────────────────────────────────────────────────────

export interface AddCommentFormData {
  comment: string
  isInternal: boolean
}

// ── Dispute response ──────────────────────────────────────────────────────────

export type DisputeAction = 'submit_evidence' | 'accept_dispute'

export interface DisputeResponseFormData {
  action: DisputeAction
  evidenceDescription?: string
  acceptanceReason?: string
  notes: string
}

// ── Refund approval ───────────────────────────────────────────────────────────

export type RefundApprovalDecision = 'approve' | 'modify' | 'decline'

export interface RefundApprovalFormData {
  decision: RefundApprovalDecision
  /** Amount in cents, used when decision === 'modify' */
  modifiedAmount?: number
  reason: string
  notes: string
}

// ── Chargeback response ───────────────────────────────────────────────────────

export type ChargebackAction = 'accept' | 'fight'

export interface ChargebackResponseFormData {
  action: ChargebackAction
  fightReason?: string
  evidenceDescription?: string
  notes: string
}

// ── Fraud alert ───────────────────────────────────────────────────────────────

export type FraudAlertAction = 'resolve' | 'escalate'

export interface FraudAlertFormData {
  action: FraudAlertAction
  resolution?: string
  escalationTeam?: string
  notes: string
}

// ── Payment review ────────────────────────────────────────────────────────────

export type PaymentReviewDecision = 'approve' | 'decline'

export interface PaymentReviewFormData {
  decision: PaymentReviewDecision
  reason: string
  notes: string
}
